// ADR 23.9.26p §9 — one event, and it is not `api_token_created`.
//
// The server's `api_tokens` row is the durable record of creation, rescope and
// revocation; a client event would count it a second time through a consent
// gate that drops most of it. What the server cannot see is whether the user
// kept the secret, so the one event here is the reveal panel closing, with how
// it was copied. No name, prefix, id or token is ever a property.
//
// A wrapper over `capture()` in `features/usage/analytics.ts`'s shape, so it is
// consent-gated by construction: `capture()` is a no-op until telemetry has
// constructed PostHog behind the consent read.
//
// ADR 25.9.26k §11 — moved into the console, which gains its own close event
// with the same reasoning (the `oauth_clients` rows are the record). `source`
// separates a create's reveal from a rotation's. The token page's own event
// was deleted with the page by ADR 25.9.26m; Infra's PostHog dashboard reads
// this one instead.

import { capture } from '../../telemetry/capture'
import type { CopyState } from './reveal/secretReveal'

//
// ADR 29.9.26d §4 — the usage-billing near-limit line, by the same rule: the
// server counts every cap write (`api_billing_requests_total`), but not that the
// line was seen or "Raise limit" pressed (which only focuses the field). No
// cap, amount or client id is ever a property; the band is all that travels.

export const DEVELOPER_EVENTS = {
  clientSecretRevealClosed: 'oauth_client_secret_reveal_closed',
  apiBillingNearLimitAction: 'api_billing_near_limit_action',
  adminConsoleSwitched: 'admin_console_switched',
  webhookSecretRevealClosed: 'webhook_secret_reveal_closed',
  appSecretRevealClosed: 'app_secret_reveal_closed',
  connectedAppRemoved: 'connected_app_removed',
  developerToolsToggled: 'developer_tools_toggled',
  headerEntryPinned: 'header_entry_pinned',
  builderRecipeStarted: 'builder_recipe_started',
  builderFlowApplied: 'builder_flow_applied',
  builderPlaygroundHandedOff: 'builder_playground_handed_off',
  builderScriptUnavailable: 'builder_script_unavailable',
  builderReferenceOpened: 'builder_reference_opened',
  builderViewChanged: 'builder_view_changed',
  builderFieldsRestored: 'builder_fields_restored',
  builderCodeParseFailed: 'builder_code_parse_failed',
  usageBillingCheckoutOpened: 'usage_billing_checkout_opened',
  usageBillingCheckoutCompleted: 'usage_billing_checkout_completed',
  usageBillingCheckoutCancelled: 'usage_billing_checkout_cancelled',
  usageBillingReturnResolved: 'usage_billing_return_resolved',
} as const

export type NearLimitAction = 'shown' | 'raise_pressed'

/**
 * ADR 30.9.26s §4 — the header switch was pressed; `to` is the console it
 * opened. No `web_` prefix, like its siblings: the console is web-only.
 * ADR 2.10.26d §7 — the header now opens `connections`; `admin` is the card's
 * "Open developer tools", still "entered the console".
 * ADR 3.10.26f §4 — the header's box of doors: `settings` joins, and `admin`
 * also counts the box's Developer row and pinned wrench.
 */
export function adminConsoleSwitched(to: 'settings' | 'connections' | 'admin' | 'app'): void {
  capture(DEVELOPER_EVENTS.adminConsoleSwitched, { to })
}

/**
 * ADR 3.10.26f §4 — a header door was pinned or unpinned. `entry` is spelled
 * here rather than imported from `layout/`, so this folder takes no
 * dependency on the header; no account, client, install or app id travels.
 */
export function headerEntryPinned(entry: 'settings' | 'developer' | 'connections', pinned: boolean): void {
  capture(DEVELOPER_EVENTS.headerEntryPinned, { entry, pinned })
}

/**
 * ADR 2.10.26d §7 (roadmap 2.10.26b D9) — one successful write of the
 * developer-tools switch. `surface` is the page the switch sat on; no app,
 * client or install id or name is ever a property.
 */
export function developerToolsToggled(shown: boolean, surface: 'header' | 'account' | 'admin_url'): void {
  capture(DEVELOPER_EVENTS.developerToolsToggled, { shown, surface })
}

/** The near-limit line was shown, or its "Raise limit" pressed; `percent` is the floored `usagePercent`. */
export function apiBillingNearLimitAction(action: NearLimitAction, percent: number): void {
  capture(DEVELOPER_EVENTS.apiBillingNearLimitAction, {
    action,
    percent_band: percent >= 100 ? 100 : 90,
  })
}

/** The console's reveal (a create or a rotation) left the pair for `idle`. */
export function oauthClientSecretRevealClosed(reveal: {
  copy: CopyState
  source: 'create' | 'rotate'
  scopeCount: number
}): void {
  capture(DEVELOPER_EVENTS.clientSecretRevealClosed, {
    copy: reveal.copy,
    source: reveal.source,
    scope_count: reveal.scopeCount,
  })
}

/**
 * ADR 30.9.26y §14 — the webhook signing secret's reveal left the pair, by
 * 25.9.26k §11's reasoning: the server's rows record every create, rotation,
 * test and redelivery; only whether the owner kept the secret is unseen. No
 * URL, id, client id, prefix or secret is ever a property.
 */
export function webhookSecretRevealClosed(reveal: {
  copy: CopyState
  source: 'create' | 'rotate'
  eventTypeCount: number
}): void {
  capture(DEVELOPER_EVENTS.webhookSecretRevealClosed, {
    copy: reveal.copy,
    source: reveal.source,
    event_type_count: reveal.eventTypeCount,
  })
}

/**
 * ADR 30.9.26ai §14 — an app's signing secret's reveal left the pair, by the
 * same reasoning: A1's audit rows record every create and rotation. `copy`
 * and `source` only; no app id, name, prefix or secret.
 */
export function appSecretRevealClosed(reveal: { copy: CopyState; source: 'create' | 'rotate' }): void {
  capture(DEVELOPER_EVENTS.appSecretRevealClosed, { copy: reveal.copy, source: reveal.source })
}

/**
 * Backend 25.9.26n §12 — a learner removed a connected app. The server writes
 * the `oauth_grant_revoked` audit row and counts `oauth_grant_revoked_total`;
 * what it cannot tell is that the press came from this page. `installed`
 * says whether the removal also uninstalled an app. No grant id, client
 * name, host or scope is ever a property.
 */
export function connectedAppRemoved(removal: { installed: boolean }): void {
  capture(DEVELOPER_EVENTS.connectedAppRemoved, { installed: removal.installed })
}

/** ADR 2.10.26g §3 — the wizard's three recipes (roadmap 2.10.26b D7); spelled here so C6 imports one union. */
export type BuilderRecipe = 'call_lingara' | 'lesson_plan_ready' | 'show_card'

/** ADR 2.10.26g §11 — which editor pressed Apply or Check. */
export type BuilderEditor = 'wizard' | 'canvas'

/**
 * ADR 2.10.26g §11 (roadmap 2.10.26b D9) — the first save of a recipe made a
 * flow. Looking at a recipe and leaving is not a start. No name or id travels.
 */
export function builderRecipeStarted(recipe: BuilderRecipe): void {
  capture(DEVELOPER_EVENTS.builderRecipeStarted, { recipe })
}

/**
 * ADR 2.10.26g §11 — an apply's outcome (`ok` / `partial`), or a Check whose
 * dry-run refused (`refused`). `nodes` is the definition's node count; no
 * name, URL, handle, flow id, object id, scope or script is ever a property.
 */
export function builderFlowApplied(applied: {
  outcome: 'ok' | 'refused' | 'partial'
  nodes: number
  editor: BuilderEditor
  params: ParamsBand
}): void {
  capture(DEVELOPER_EVENTS.builderFlowApplied, {
    outcome: applied.outcome,
    nodes: applied.nodes,
    editor: applied.editor,
    params: applied.params,
  })
}

/** ADR 8.10.26t §2 — whether any node stores a setting value; a band, never a count, name or value. */
export type ParamsBand = 'none' | 'some'

/** ADR 8.10.26t §2 — the one rule every `builderFlowApplied` caller bands `params` by. */
export function paramsBand(definition: { nodes: ReadonlyArray<{ params: Record<string, unknown> }> }): ParamsBand {
  return definition.nodes.some((node) => Object.keys(node.params).length > 0) ? 'some' : 'none'
}

/**
 * ADR 8.10.26t §1 — a describe pass could not run the script (`unavailable`:
 * the sandbox never started; `timeout`: the script spent its budget), so the
 * inspector drew no settings. `code` only; no handle, script, param or value.
 */
export function builderScriptUnavailable(code: 'unavailable' | 'timeout'): void {
  capture(DEVELOPER_EVENTS.builderScriptUnavailable, { code })
}

/** ADR 8.10.26h §10 — a builder step kind, spelled here like `BuilderRecipe` so `admin/` imports nothing from `builder/`. */
export type BuilderKind = 'client' | 'secret' | 'webhook' | 'app'

/**
 * ADR 8.10.26h §10 — the playground handed a script to a new canvas. `kind`
 * is the script step's kind; no script, sample, param or name travels. A Run
 * is not counted: it is local and frequent, so a count would measure typing.
 */
export function builderPlaygroundHandedOff(kind: BuilderKind): void {
  capture(DEVELOPER_EVENTS.builderPlaygroundHandedOff, { kind })
}

/** ADR 9.10.26j §8 — how the script reference was opened: its toggle, or a warning's "About this rule". */
export type BuilderReferenceVia = 'toggle' | 'rule'

/** ADR 9.10.26j §8 — which host drew it; spelled here, like `BuilderKind`, so `admin/` imports nothing from `builder/`. */
export type BuilderReferenceSurface = 'playground' | 'canvas'

/**
 * ADR 9.10.26j §8 (roadmap 9.10.26f D5) — the script reference went from
 * closed to open. A drawer restored open on mount is not an act and is not
 * counted. No rule id, kind, section, helper name or script ever travels.
 */
export function builderReferenceOpened(opened: { via: BuilderReferenceVia; surface: BuilderReferenceSurface }): void {
  capture(DEVELOPER_EVENTS.builderReferenceOpened, { via: opened.via, surface: opened.surface })
}

/** ADR 15.10.26j §8 — spelled here, like `BuilderKind`, so `admin/` imports nothing from `builder/`; `code` since 15.10.26m. */
export type BuilderView = 'fields' | 'canvas' | 'code'

/**
 * ADR 15.10.26j §8 (roadmap 15.10.26i D7) — a connection page's tab press, or
 * its Side by side press (the current tab as `to`, the new state as
 * `side_by_side`). A default view, a redirect, a reload, a resize and a
 * create's URL replacement are not acts and are not counted. No flow id,
 * handle or name ever travels.
 */
export function builderViewChanged(changed: { to: BuilderView; sideBySide: boolean }): void {
  capture(DEVELOPER_EVENTS.builderViewChanged, { to: changed.to, side_by_side: changed.sideBySide })
}

/**
 * ADR 15.10.26n §8 (roadmap 15.10.26i D7) — "Use fields instead" pressed: a
 * script of literals went back to fields. Only `kind` travels; no script,
 * field value, handle or reason. A reason line being shown is not counted.
 */
export function builderFieldsRestored(kind: BuilderKind): void {
  capture(DEVELOPER_EVENTS.builderFieldsRestored, { kind })
}

/** ADR 15.10.26m §8 — V3's error codes, spelled here so `admin/` imports nothing from `builder/`. */
export type BuilderCodeProblem =
  | 'syntax' | 'header' | 'header_version' | 'comment' | 'statement' | 'kind' | 'handle' | 'handle_taken' | 'arguments' | 'options'
  | 'value' | 'text' | 'number' | 'key' | 'reserved_key' | 'client' | 'script' | 'script_name' | 'setting'
  // ADR 16.10.26m §2 §3: a script named apart from its step, and a node file's two refusals.
  | 'name_mismatch' | 'file_statements' | 'file_kind'

/** ADR 15.10.26m §8 — a Code tab's text stopped reading, once per episode. `code` only: no text, handle, line, name or value. */
export function builderCodeParseFailed(code: BuilderCodeProblem): void {
  capture(DEVELOPER_EVENTS.builderCodeParseFailed, { code })
}

/** ADR 14.10.26g §3 — why the in-console form gave way to the hosted page. */
export type CheckoutFallbackReason = 'script_blocked' | 'unavailable'

/**
 * ADR 14.10.26g §3 — usage-billing setup opened: since 14.10.26n the
 * Lingara-drawn form mounted Stripe's Payment Element (`elements`, in place of
 * 14.10.26g's `embedded`), or the hosted fallback navigated (with why). These
 * answer how often Stripe.js is blocked, which decides whether the fallback can
 * ever go. No session id, secret, key or amount ever travels.
 */
export function usageBillingCheckoutOpened(
  opened: { mode: 'elements' } | { mode: 'hosted_fallback'; reason: CheckoutFallbackReason },
): void {
  capture(DEVELOPER_EVENTS.usageBillingCheckoutOpened, { ...opened })
}

/** ADR 14.10.26n §3 — `confirm` succeeded (or answered `409`: the account already bills). */
export function usageBillingCheckoutCompleted(): void {
  capture(DEVELOPER_EVENTS.usageBillingCheckoutCompleted)
}

/** ADR 14.10.26g §3 — the setup frame's Cancel. */
export function usageBillingCheckoutCancelled(): void {
  capture(DEVELOPER_EVENTS.usageBillingCheckoutCancelled)
}

/** ADR 14.10.26j §1 — what started the re-read: the hosted return, the in-page confirm, or the pending line's Refresh. */
export type BillingReturnTrigger = 'redirect' | 'confirm' | 'refresh'

/**
 * ADR 14.10.26j §1 — the post-payment re-read ended: a read said `active`, or
 * the bound came first (`pending`). `poll_count` is the learner event's own key
 * (`features/billing/analytics.ts`); a cancelled loop sends nothing. No
 * account, client, session or amount ever travels.
 */
export function usageBillingReturnResolved(resolved: {
  result: 'active' | 'pending'
  pollCount: number
  trigger: BillingReturnTrigger
}): void {
  capture(DEVELOPER_EVENTS.usageBillingReturnResolved, {
    result: resolved.result,
    poll_count: resolved.pollCount,
    trigger: resolved.trigger,
  })
}
